import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, SquareArrowOutUpRight, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalendlyModal, CALENDLY_URLS } from "@/components/CalendlyModal";
import { useTheme, getThemeColors } from "@/context/ThemeContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_birth-ready/artifacts/p71eeafq_tree%20blue%20.png";

// Static colors for nav (nav has special handling)
const COLORS = {
  warmStone: "#faf7f2",
  oliveSage: "#a07830",
  oliveSageHover: "#8a6628",
  mineralTaupe: "rgba(28,21,16,0.18)",
  warmGold: "#a07830",
  warmGoldHover: "#8a6628",
  inkGrey: "#1c1510",
  // Navy for buttons
  inkNavy: "#1c1510",
  inkNavyHover: "#1c1510",
  softWhite: "#faf7f2",
};

// Pages with full-bleed hero images where nav needs light text
// Editorial palette migration moved all formerly-dark heroes to cream backgrounds.
// Nav now uses dark text by default; only DARK_HEADER_PAGES need light text.
const HERO_PAGES = [];

// Pages with dark headers where nav needs light text AND gold CTA button
const DARK_HEADER_PAGES = [
  "/quiz",
  "/results/",  // Will match any results page
  "/evidence",
];

// Check if current path matches dark header pages
const isDarkHeaderPage = (pathname) => {
  return DARK_HEADER_PAGES.some(page => pathname.startsWith(page));
};

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  // Determine which funnel we're in
  const isSharedLanding = location.pathname === "/";
  const isLeadershipFunnel = location.pathname.startsWith("/leadership");

  // Check if current page has a full-bleed hero
  const isHeroPage = HERO_PAGES.includes(location.pathname);
  
  // Check if current page has a dark header (quiz, results)
  const hasDarkHeader = isDarkHeaderPage(location.pathname);
  
  // Combined check for light text nav
  const needsLightNav = isHeroPage || hasDarkHeader;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navigation links change based on which funnel we're in
  const getNavLinks = () => {
    if (isSharedLanding) {
      return [
        { href: "/leadership", label: "Leadership", isInternal: true },
        { href: "/leadership/about", label: "The Work", isInternal: true },
        { href: "/leadership/services", label: "Services", isInternal: true, hasDropdown: true },
        { href: "/assessment", label: "Assessment", isInternal: true },
        { href: "/leadership/faq", label: "FAQ", isInternal: true },
        { href: "/blog", label: "Blog", isInternal: true },
      ];
    }
    
    // Leadership funnel (default)
    return [
      { href: "/leadership", label: "Leadership", isInternal: true },
      { href: "/leadership/about", label: "The Work", isInternal: true },
      { href: "/leadership/services", label: "Services", isInternal: true, hasDropdown: true },
      { href: "/assessment", label: "Assessment", isInternal: true },
      { href: "/leadership/faq", label: "FAQ", isInternal: true },
      { href: "/blog", label: "Blog", isInternal: true },
    ];
  };

  const navLinks = getNavLinks();

  const leadershipServices = [
    { href: "/leadership/services/focused-session", label: "Focused Stress Session" },
    { href: "/leadership/services/individual-session", label: "Single Session" },
    { href: "/leadership/services/10-session-program", label: "10-Session Series" },
    { href: "/leadership/services/12-month-immersion", label: "Leadership Stability Structure" },
    { href: "/leadership/scaling", label: "Scaling Leadership" },
  ];

  const isActive = (path) => location.pathname === path;

  // Determine text color based on scroll state and page type
  const getTextColor = () => {
    if (isScrolled) {
      return "text-ink";
    }
    if (needsLightNav) {
      return "text-white font-medium drop-shadow-md";
    }
    return "text-ink";
  };

  const getLinkColor = (isLinkActive) => {
    if (isScrolled) {
      return isLinkActive 
        ? "text-gold font-semibold" 
        : "text-ink hover:text-gold";
    }
    if (needsLightNav) {
      return isLinkActive 
        ? "text-white font-semibold drop-shadow-md" 
        : "text-white/90 hover:text-white drop-shadow-md";
    }
    return isLinkActive 
      ? "text-gold font-semibold" 
      : "text-ink hover:text-gold";
  };

  // Logo filter - olive sage when scrolled, white on hero/dark pages
  const getLogoStyle = () => {
    if (isScrolled) {
      // Olive sage color filter
      return { 
        filter: 'brightness(0) saturate(100%)',
        opacity: 0.55
      };
    }
    if (needsLightNav) {
      return { 
        filter: 'brightness(0) invert(1)',
        opacity: 0.95
      };
    }
    return { 
      filter: 'brightness(0) saturate(100%)',
      opacity: 0.55
    };
  };

  const logoLink = "/";

  return (
    <nav
      data-testid="main-navigation"
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "nav-glass py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to={logoLink}
            data-testid="nav-logo"
            aria-label="Innate Perspectives - Go to homepage"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img
              src={LOGO_URL}
              alt="Innate Perspectives tree logo representing growth and grounded leadership"
              className="w-10 h-10 transition-all duration-300"
              style={getLogoStyle()}
            />
            <span 
              className={`font-serif text-xl md:text-2xl transition-all duration-300 ${
                isScrolled 
                  ? 'text-[#1c1510]' 
                  : needsLightNav 
                    ? 'text-white drop-shadow-md' 
                    : 'text-[#1c1510]'
              }`}
              aria-hidden="true"
            >
              Innate Perspectives
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6" role="menubar">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.href} ref={servicesRef} className="relative">
                  <button
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                    data-testid="nav-services-dropdown"
                    aria-expanded={isServicesOpen}
                    aria-haspopup="true"
                    aria-label="Services menu - click to expand"
                    className={`flex items-center gap-1 text-[14px] tracking-wide transition-all duration-300 ${
                      isScrolled 
                        ? location.pathname.includes('/services') 
                          ? 'text-[#a07830] font-medium' 
                          : 'text-[#1c1510]/85 hover:text-[#8a6628]'
                        : needsLightNav
                          ? location.pathname.includes('/services')
                            ? 'text-white font-medium drop-shadow-md'
                            : 'text-white/90 hover:text-white drop-shadow-md'
                          : location.pathname.includes('/services')
                            ? 'text-[#a07830] font-medium'
                            : 'text-[#1c1510]/85 hover:text-[#8a6628]'
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  
                  {isServicesOpen && (
                    <div 
                      className="absolute top-full left-0 mt-2 w-64 bg-[#faf7f2] border border-[rgba(28,21,16,0.18)]/30 shadow-lg py-2 z-50"
                      role="menu"
                      aria-label="Services submenu"
                    >
                      <Link
                        to="/leadership/services"
                        className="block px-4 py-2 text-sm text-[#1c1510] hover:bg-[#ede8de] font-medium"
                        role="menuitem"
                      >
                        All Services
                      </Link>
                      <div className="border-t border-[rgba(28,21,16,0.18)]/30 my-2" role="separator" />
                      {leadershipServices.map((service) => (
                        <Link
                          key={service.href}
                          to={service.href}
                          className="block px-4 py-2 text-sm text-[#a07830] hover:bg-[#ede8de] hover:text-[#8a6628]"
                          role="menuitem"
                        >
                          {service.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : link.isInternal === false ? (
                // External link - opens in new tab with external icon
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`text-[14px] tracking-wide transition-all duration-300 flex items-center gap-1 ${
                    isScrolled 
                      ? 'text-[#1c1510]/85 hover:text-[#8a6628]'
                      : needsLightNav
                        ? 'text-white/90 hover:text-white drop-shadow-md'
                        : 'text-[#1c1510]/85 hover:text-[#8a6628]'
                  }`}
                >
                  {link.label}
                  <SquareArrowOutUpRight size={12} />
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`text-[14px] tracking-wide transition-all duration-300 ${
                    isScrolled 
                      ? isActive(link.href) 
                        ? 'text-[#a07830] font-medium' 
                        : 'text-[#1c1510]/85 hover:text-[#8a6628]'
                      : needsLightNav
                        ? isActive(link.href)
                          ? 'text-white font-medium drop-shadow-md'
                          : 'text-white/90 hover:text-white drop-shadow-md'
                        : isActive(link.href)
                          ? 'text-[#a07830] font-medium'
                          : 'text-[#1c1510]/85 hover:text-[#8a6628]'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Desktop CTA - Gold on dark pages, Navy otherwise */}
          <div className="hidden md:flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                isScrolled || !needsLightNav 
                  ? 'text-[#1c1510] hover:bg-[#4A4742]/10' 
                  : 'text-white hover:bg-white/10'
              }`}
            >
              {theme === 'dark' ? (
                <Sun size={18} aria-hidden="true" />
              ) : (
                <Moon size={18} aria-hidden="true" />
              )}
            </button>
            
            <Button 
              onClick={() => setIsCalendlyOpen(true)}
              data-testid="nav-clarity-call-btn"
              size="sm"
              className={`rounded-full px-4 py-1.5 text-[12px] font-medium tracking-wide transition-all duration-300 ${
                isScrolled || !needsLightNav
                  ? ""
                  : "backdrop-blur-sm"
              }`}
              style={{ 
                backgroundColor: hasDarkHeader && !isScrolled ? COLORS.warmGold : COLORS.inkNavy, 
                color: hasDarkHeader && !isScrolled ? COLORS.inkNavy : COLORS.softWhite 
              }}
            >
              Clarity Call
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            className={`md:hidden p-2 transition-all duration-300 ${
              isScrolled || !needsLightNav ? "text-[#1c1510]" : "text-white drop-shadow-md"
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            id="mobile-menu"
            data-testid="mobile-menu"
            role="menu"
            aria-label="Mobile navigation menu"
            className="md:hidden absolute top-full left-0 right-0 bg-[#faf7f2] border-b border-[rgba(28,21,16,0.18)]/30 py-6 px-6"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                link.isInternal === false ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                    role="menuitem"
                    aria-label={`${link.label} - opens in new tab`}
                    className="text-sm uppercase tracking-wider py-2 text-[#1c1510]/85 flex items-center gap-1.5"
                  >
                    {link.label}
                    <SquareArrowOutUpRight size={12} aria-hidden="true" />
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                    role="menuitem"
                    className={`text-sm uppercase tracking-wider py-2 ${
                      isActive(link.href) 
                        ? "text-[#a07830] font-semibold" 
                        : "text-[#1c1510]/85"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              
              {/* Mobile Services Submenu */}
              {isLeadershipFunnel && (
                <div className="border-t border-[rgba(28,21,16,0.18)]/30 pt-4 mt-2" role="group" aria-label="Leadership Services">
                  <span className="text-xs uppercase tracking-wider text-[#a07830] mb-2 block" id="mobile-services-label">
                    Leadership Services
                  </span>
                  {leadershipServices.map((service) => (
                    <Link
                      key={service.href}
                      to={service.href}
                      className="block text-sm text-[#1c1510]/85 py-2 pl-4 hover:text-[#8a6628]"
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              )}
              
              {/* Theme Toggle - Mobile */}
              <div className="flex items-center justify-between border-t border-[rgba(28,21,16,0.18)]/30 pt-4 mt-2">
                <span className="text-sm text-[#1c1510]/85">
                  {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                </span>
                <button
                  onClick={toggleTheme}
                  data-testid="mobile-theme-toggle"
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  className="p-2 rounded-full text-[#1c1510] hover:bg-[#4A4742]/10 transition-all"
                >
                  {theme === 'dark' ? (
                    <Sun size={20} aria-hidden="true" />
                  ) : (
                    <Moon size={20} aria-hidden="true" />
                  )}
                </button>
              </div>
              
              <Button 
                onClick={() => {
                  setIsCalendlyOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                data-testid="mobile-clarity-call-btn"
                className="w-full rounded-full px-6 py-3 text-[14px] font-medium tracking-wide mt-4"
                style={{ backgroundColor: COLORS.inkNavy, color: COLORS.softWhite }}
              >
                Clarity Call
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Calendly Modal */}
      <CalendlyModal 
        isOpen={isCalendlyOpen} 
        onClose={() => setIsCalendlyOpen(false)} 
        calendlyUrl={CALENDLY_URLS.clarityCall}
        title="Book a Clarity Call"
      />
    </nav>
  );
}
